import React from 'react';
import { cn } from '../../../lib/utils';

export interface LensPresetChip {
  id: string;
  label: string;
  icon: React.ElementType;
  description?: string;
  isActive: boolean;
  /** Shown but not toggleable; `description` should say why. */
  disabled?: boolean;
  onToggle: () => void;
}

export interface LensPresetGroup {
  /** Uppercase tier label shown above the chip row (omit for a single ungrouped row). */
  title?: string;
  chips: LensPresetChip[];
}

interface LensPresetSectionProps {
  groups: LensPresetGroup[];
  /** Shown only when at least one chip is active. */
  onClearAll?: () => void;
  clearLabel?: string;
  /** Optional helper line under the chips (e.g. the chat escape-hatch hint). */
  helperText?: React.ReactNode;
  className?: string;
}

/**
 * One-click lens chips — the shared presentation for the preset UX first
 * built in `NetworkLensPresets` and `ProcessLensPresets`. Collapses the
 * filter/lens rule builder into named, discoverable pills that toggle a
 * single view rule each. Domains supply the chips (with their own
 * isActive/onToggle wiring); this owns the aesthetic and the layout.
 */
export const LensPresetSection: React.FC<LensPresetSectionProps> = ({
  groups,
  onClearAll,
  clearLabel = 'Clear lenses',
  helperText,
  className,
}) => {
  const visibleGroups = groups.filter((group) => group.chips.length > 0);
  if (visibleGroups.length === 0) return null;

  const anyActive = visibleGroups.some((group) => group.chips.some((chip) => chip.isActive));

  return (
    <div className={cn('space-y-3', className)}>
      {visibleGroups.map((group, index) => (
        <div key={group.title ?? `group-${index}`}>
          {group.title && (
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.title}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {group.chips.map((chip) => {
              const Icon = chip.icon;
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={chip.disabled ? undefined : chip.onToggle}
                  title={chip.description}
                  aria-pressed={chip.isActive}
                  aria-disabled={chip.disabled || undefined}
                  data-testid={`lens-preset-${chip.id}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                    chip.isActive
                      ? 'border-primary bg-primary text-primary-foreground'
                      : chip.disabled
                        ? 'cursor-not-allowed border-dashed border-border bg-background text-muted-foreground opacity-60'
                        : 'border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {(helperText || (anyActive && onClearAll)) && (
        <div className="flex items-center justify-between gap-2">
          {helperText ? (
            <p className="text-[11px] text-muted-foreground">{helperText}</p>
          ) : (
            <span />
          )}
          {anyActive && onClearAll && (
            <button
              type="button"
              onClick={onClearAll}
              className="ml-2 shrink-0 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            >
              {clearLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
